import { create } from 'zustand';

export type ServerCacheState = 'unknown' | 'fresh' | 'stale';

export type ServerHealthRecord = {
  cacheState: ServerCacheState;
  lastSyncAt: number | null;
  lastSyncAttemptAt: number | null;
  lastSyncFailedAt: number | null;
  lastSyncError: string | null;
};

export const SERVER_CACHE_VISIBLE_AFTER_MS = 10 * 60 * 1000;

export function shouldShowCachedServerState(
  health: Pick<ServerHealthRecord, 'cacheState' | 'lastSyncAt'> | null | undefined,
  nowMs: number
): boolean {
  return health?.cacheState === 'stale'
    && typeof health.lastSyncAt === 'number'
    && Number.isFinite(health.lastSyncAt)
    && nowMs - health.lastSyncAt > SERVER_CACHE_VISIBLE_AFTER_MS;
}

type ServerHealthState = {
  byServerId: Record<string, ServerHealthRecord>;
  markSyncSuccess: (serverId: string) => void;
  markSyncFailure: (serverId: string, error?: string | null) => void;
  resetServerHealth: (serverId: string) => void;
};

const DEFAULT_RECORD: ServerHealthRecord = {
  cacheState: 'unknown',
  lastSyncAt: null,
  lastSyncAttemptAt: null,
  lastSyncFailedAt: null,
  lastSyncError: null
};

function getExisting(state: ServerHealthState, serverId: string): ServerHealthRecord {
  return state.byServerId[serverId] || DEFAULT_RECORD;
}

export const useServerHealthStore = create<ServerHealthState>((set) => ({
  byServerId: {},

  markSyncSuccess: (serverId) => set((state) => {
    if (!serverId) return state;
    const now = Date.now();
    const existing = getExisting(state as ServerHealthState, serverId);
    return {
      byServerId: {
        ...state.byServerId,
        [serverId]: {
          ...existing,
          cacheState: 'fresh',
          lastSyncAt: now,
          lastSyncAttemptAt: now,
          lastSyncError: null
        }
      }
    };
  }),

  markSyncFailure: (serverId, error) => set((state) => {
    if (!serverId) return state;
    const now = Date.now();
    const existing = getExisting(state as ServerHealthState, serverId);
    return {
      byServerId: {
        ...state.byServerId,
        [serverId]: {
          ...existing,
          cacheState: existing.lastSyncAt ? 'stale' : 'unknown',
          lastSyncAttemptAt: now,
          lastSyncFailedAt: now,
          lastSyncError: (error || '').trim() || existing.lastSyncError || null
        }
      }
    };
  }),

  resetServerHealth: (serverId) => set((state) => {
    if (!serverId) return state;
    const next = { ...state.byServerId };
    delete next[serverId];
    return { byServerId: next };
  })
}));
